/**
 * 사람이 눌러야 진행되는 지점을 화면 맨 위에 크게 알린다 (계획서 §6.4, §13.4).
 *
 * A/B 승인과 인간 가동은 이 데모의 핵심 장면인데, 버튼과 안내가 우측 패널 안쪽에
 * 있어 시연 중에 눈에 띄지 않았다. 시계가 멈춘 이유를 좌측 구석 한 줄로만 알리면
 * 사람이 "화면이 멈췄다"고 오해한다.
 *
 * 화면을 덮는 modal 로 만들지 않는다. A/B 비교표를 읽어야 어느 안을 고를지 정할 수
 * 있으므로, 표를 가리면 안 된다. 대신 상단 전체 너비에 고정하고 버튼을 함께 둔다.
 */

import type { RunSnapshot, StrategyCode } from "@/types/api";
import { approvalGradeLabel } from "@/lib/format";

interface Props {
  snapshot: RunSnapshot;
  busy: boolean;
  onApprove: (strategy: StrategyCode) => void;
  onHumanStart: () => void;
  onConfirmRisk: (confirmed: boolean) => void;
}

export function ActionBanner({
  snapshot,
  busy,
  onApprove,
  onHumanStart,
  onConfirmRisk,
}: Props): React.JSX.Element | null {
  const { clock, plan } = snapshot;
  const timer = snapshot.approval_timer_remaining_seconds;

  if (clock.state === "WAIT_PLAN_APPROVAL" && plan.candidates) {
    const recommended = plan.recommended;
    const grade = recommended ? plan.candidates[recommended].approval_grade : null;

    return (
      <section className="action-banner" role="alert" aria-live="assertive">
        <div className="action-banner-text">
          <p className="action-banner-title">A안·B안 중 하나를 승인해 주세요</p>
          <p className="action-banner-detail">
            승인해야 가상시계가 다시 흐릅니다.
            {recommended && (
              <>
                {" "}
                <strong>{recommended}안 추천</strong>
                <span className="source-tag source-rule">규칙</span>
                {grade && ` · ${approvalGradeLabel(grade)}`}
              </>
            )}
            {timer !== null && ` · 자동승인까지 ${String(timer)}초`}
          </p>
        </div>
        <div className="action-banner-actions">
          {(["A", "B"] as StrategyCode[]).map((code) => (
            <button
              key={code}
              type="button"
              className={code === recommended ? "primary" : "secondary"}
              onClick={() => {
                onApprove(code);
              }}
              disabled={busy}
            >
              계획 {code} 승인
              {code === recommended && " (추천)"}
            </button>
          ))}
        </div>
      </section>
    );
  }

  if (clock.state === "WAIT_HUMAN_START") {
    return (
      <section className="action-banner" role="alert" aria-live="assertive">
        <div className="action-banner-text">
          <p className="action-banner-title">설비 가동을 승인해 주세요</p>
          <p className="action-banner-detail">
            기동시각에 도달했습니다. <strong>자동승인 등급이어도 설비 가동은 사람이
            눌러야 합니다</strong> (§6.4).
          </p>
        </div>
        <div className="action-banner-actions">
          <button type="button" className="primary" onClick={onHumanStart} disabled={busy}>
            가동 승인
          </button>
        </div>
      </section>
    );
  }

  if (clock.state === "WAIT_RISK_CONFIRMATION") {
    return (
      <section className="action-banner" role="alert" aria-live="assertive">
        <div className="action-banner-text">
          <p className="action-banner-title">조기 수리 완료의 위험을 확인해 주세요</p>
          <p className="action-banner-detail">
            답해야 계획 수립이 이어집니다.
          </p>
        </div>
        <div className="action-banner-actions">
          <button
            type="button"
            className="primary"
            onClick={() => {
              onConfirmRisk(true);
            }}
            disabled={busy}
          >
            확인하고 진행
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              onConfirmRisk(false);
            }}
            disabled={busy}
          >
            취소
          </button>
        </div>
      </section>
    );
  }

  return null;
}
